import { useState } from 'react';
import { Receipt, CheckCircle2, XCircle, Clock, ShieldCheck, RotateCcw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api-client';
import { formatDate, formatUsd, cn } from '@/lib/utils';

type OrderRow = {
  id: string;
  organization_id: string;
  user_email: string | null;
  org_name: string | null;
  package_id: string;
  package_name: string | null;
  payment_method: string | null;
  amount_paid: number | null;
  currency: string | null;
  status: 'pending' | 'pending_verification' | 'paid' | 'failed' | 'refunded';
  credits_granted: number | null;
  created_at: number;
  paid_at: number | null;
  crypto_asset?: string | null;
  crypto_network?: string | null;
};

type OrdersResponse = {
  orders: OrderRow[];
  totals: {
    paid_try_kurus: number;
    paid_usd_cents: number;
    paid_count: number;
    pending_count: number;
    failed_count: number;
  };
};

function formatAmount(amount: number | null, currency: string | null): string {
  if (amount == null || !currency) return '—';
  const value = amount / 100;
  const locale = currency === 'try' ? 'tr-TR' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(value);
}

export function PlatformOrdersPage() {
  const [method, setMethod] = useState<'' | 'stripe' | 'crypto' | 'manual'>('');
  const [status, setStatus] = useState<'' | 'paid' | 'pending' | 'pending_verification' | 'failed'>('');

  const { data, isPending } = useQuery({
    queryKey: ['platform', 'orders', method, status],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (method) qs.set('method', method);
      if (status) qs.set('status', status);
      return api.get<OrdersResponse>(`/api/platform/orders${qs.toString() ? `?${qs.toString()}` : ''}`);
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Receipt className="w-5 h-5" /> Tüm Siparişler
        </h1>
        <p className="text-muted-foreground text-sm">
          Platform geneli sipariş ve ödeme kayıtları.
        </p>
      </div>

      {data?.totals && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Metric label="Ödenen (TRY)" value={formatAmount(data.totals.paid_try_kurus, 'try')} />
          <Metric label="Ödenen (USD)" value={formatAmount(data.totals.paid_usd_cents, 'usd')} />
          <Metric label="Başarılı" value={String(data.totals.paid_count)} color="text-emerald-600 dark:text-emerald-400" />
          <Metric label="Bekleyen" value={String(data.totals.pending_count)} color="text-amber-600 dark:text-amber-400" />
          <Metric label="Başarısız" value={String(data.totals.failed_count)} color="text-destructive" />
        </div>
      )}

      <Card>
        <CardContent className="p-3 flex items-center gap-2 flex-wrap">
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as typeof method)}
            className="flex h-9 rounded-md border border-input bg-background px-3 text-xs"
          >
            <option value="">Tüm yöntemler</option>
            <option value="stripe">Stripe (kart)</option>
            <option value="crypto">Kripto</option>
            <option value="manual">Manuel grant</option>
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
            className="flex h-9 rounded-md border border-input bg-background px-3 text-xs"
          >
            <option value="">Tüm durumlar</option>
            <option value="paid">Ödendi</option>
            <option value="pending">Bekliyor</option>
            <option value="pending_verification">Doğrulanıyor</option>
            <option value="failed">Başarısız</option>
          </select>
        </CardContent>
      </Card>

      {isPending ? (
        <div className="text-muted-foreground">Yükleniyor…</div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b">
                <tr>
                  <th className="text-left py-2 px-3 font-medium">Tarih</th>
                  <th className="text-left py-2 px-3 font-medium">Müşteri</th>
                  <th className="text-left py-2 px-3 font-medium">Paket</th>
                  <th className="text-left py-2 px-3 font-medium">Yöntem</th>
                  <th className="text-left py-2 px-3 font-medium">Tutar</th>
                  <th className="text-right py-2 px-3 font-medium">Kredi</th>
                  <th className="text-center py-2 px-3 font-medium">Durum</th>
                </tr>
              </thead>
              <tbody>
                {(data?.orders ?? []).map((o) => (
                  <tr key={o.id} className="border-b last:border-b-0">
                    <td className="py-2 px-3">
                      <div>{formatDate(o.paid_at ?? o.created_at)}</div>
                      <div className="text-xs text-muted-foreground font-mono">{o.id}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="text-sm">{o.org_name ?? '—'}</div>
                      <div className="text-xs text-muted-foreground truncate">{o.user_email}</div>
                    </td>
                    <td className="py-2 px-3">{o.package_name ?? o.package_id}</td>
                    <td className="py-2 px-3">
                      {o.payment_method === 'crypto' ? (
                        <span className="text-xs">🪙 {o.crypto_asset} / {o.crypto_network}</span>
                      ) : o.payment_method === 'stripe' ? (
                        <span className="text-xs">💳 Stripe</span>
                      ) : (
                        <span className="text-xs">✋ Manuel</span>
                      )}
                    </td>
                    <td className="py-2 px-3 font-mono">{formatAmount(o.amount_paid, o.currency)}</td>
                    <td className="py-2 px-3 text-right font-mono">
                      {o.credits_granted ? `+${formatUsd(o.credits_granted, 2)}` : '—'}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <StatusBadge status={o.status} />
                    </td>
                  </tr>
                ))}
                {(data?.orders ?? []).length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-muted-foreground">
                      Sonuç yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Metric({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={cn('text-lg font-semibold font-mono', color)}>{value}</div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: OrderRow['status'] }) {
  const map: Record<OrderRow['status'], { label: string; cls: string; icon: React.ComponentType<{ className?: string }> }> = {
    pending: { label: 'Bekliyor', cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-400', icon: Clock },
    pending_verification: { label: 'Doğrulanıyor', cls: 'bg-primary/10 text-primary', icon: ShieldCheck },
    paid: { label: 'Ödendi', cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
    failed: { label: 'Başarısız', cls: 'bg-destructive/10 text-destructive', icon: XCircle },
    refunded: { label: 'İade', cls: 'bg-muted text-muted-foreground', icon: RotateCcw },
  };
  const m = map[status] ?? map.pending;
  const Icon = m.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', m.cls)}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}
